<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import Button from "@/components/Button.vue";
import Input from "@/components/Input.vue";
import Modal from "@/components/Modal.vue";
import {
  QrCodeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/authStore";
import services from "@/services";
import { useToaster } from "ram-components";
import { useUserDataStore } from "@/stores/userDataStore";

const recentlyScanned = ref<any[]>([]);
const scanInput = ref("");
const isScanning = ref(false);
const showUnexpectedModal = ref(false);
const unexpectedConsignmentNo = ref("");
const recipientName = ref("");
const contactNumber = ref("");
const scanStatus = ref<"success" | "warning" | "error" | "">("");

const authStore = useAuthStore();
const userDataStore = useUserDataStore();

onMounted(() => {
  refocusInput();
  fetchRecentScans();
});

const toaster = useToaster({
  position: "bottom-right",
  closeOnClick: false,
  pauseOnHover: true,
  showCloseOnHover: true,
  timeout: 6000,
});

const fetchRecentScans = async () => {
  const userId = authStore.user?.recordID;
  if (!userId) return;

  try {
    userDataStore.setLoading(true);
    const response = await services.getRecentReceivingDetails(userId);
    const data = response.data.result;

    if (data) {
      recentlyScanned.value = data.map((item: any) => ({
        consignmentNo: item.consignmentID,
        status: item.isExpectedParcel ? "expected" : "unexpected",
      }));
    }
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load List of parcels",
    });
  } finally {
    userDataStore.setLoading(false);
  }
};

const refocusInput = async () => {
  await nextTick();
  const inputEl = document.getElementById("parcelInput") as HTMLInputElement;
  if (inputEl) {
    inputEl.readOnly = true;
    inputEl.focus({ preventScroll: true });
    setTimeout(() => {
      inputEl.readOnly = false;
    }, 50);
  }
};

const handleScan = async () => {
  await new Promise((resolve) => setTimeout(resolve, 50));
  const inputEl = document.getElementById("parcelInput") as HTMLInputElement;
  if (inputEl) inputEl.select();

  const consignmentId = scanInput.value.trim();

  if (!consignmentId || isScanning.value) {
    if (!showUnexpectedModal.value) refocusInput();
    return;
  }

  isScanning.value = true;
  scanStatus.value = "";

  try {
    const payload = {
      consignmentID: consignmentId,
      createdBy: authStore.currentUserID,
    };

    const response = await services.scanReceivedParcel(payload);
    const result = response.data.result;

    if (result) {
      const msg = (result.message || "").toLowerCase();

      if (msg.includes("already scanned")) {
        scanStatus.value = "warning";
        toaster.push({
          type: "warning",
          text: "Parcel already scanned.",
        });
      } else if (msg.includes("not valid range")) {
        scanStatus.value = "error";
        toaster.push({
          type: "error",
          text: "Invalid Consignment Number.",
        });
      } else if (msg.includes("invalid billedto")) {
        scanStatus.value = "error";
        toaster.push({
          type: "error",
          text: "Invalid billedTo for Consignment.",
        });
      } else if (result.isExpectedParcel) {
        scanStatus.value = "success";
        toaster.push({
          type: "success",
          text: "Parcel Received Successfully!",
        });
        setTimeout(()=>{
          fetchRecentScans() , 3000
        })
      } else {
        unexpectedConsignmentNo.value = consignmentId;
        showUnexpectedModal.value = true;
        scanStatus.value = "warning";
      }
    } else {
      scanStatus.value = "error";
    }
  } catch (error) {
    scanStatus.value = "error";
    toaster.push({
      type: "error",
      text: "System Error: Could not scan parcel.",
    });
  } finally {
    scanInput.value = "";
    isScanning.value = false;
    if (!showUnexpectedModal.value) refocusInput();
  }
};

const handleUnexpectedSubmit = async () => {
  if (!recipientName.value.trim() || !contactNumber.value.trim()) return;

  try {
    isScanning.value=true;
    const payload = {
      consignmentID: unexpectedConsignmentNo.value,
      receiverName: recipientName.value,
      receiverContactNumber: contactNumber.value,
      createdBy: authStore.currentUserID,
    };

    const response = await services.saveUnexpectedReceivedParcel(payload);
    const data = response.data;

    const isSuccess = data.statusCode === 200 || data.status === "SUCCEEDED";

    if (isSuccess) {
      scanStatus.value = "success";
      closeUnexpectedModal();
      toaster.push({
        type: "success",
        text: "Unexpected Parcel Received Successfully!",
      });
      await fetchRecentScans();
      refocusInput();
    } else {
      const errorMsg = data.message || data.details || "Failed to save parcel";

      toaster.push({
        type: "error",
        text: errorMsg,
      });
    }
  } catch (error: any) {
    const msg = error.response?.data?.message || "Network error occurred.";

    toaster.push({
      type: "error",
      text: msg,
    });
  }
  finally{
     isScanning.value=false;
  }
};

const closeUnexpectedModal = () => {
  showUnexpectedModal.value = false;
  unexpectedConsignmentNo.value = "";
  recipientName.value = "";
  contactNumber.value = "";
};

const getStatusClass = (parcel: any) => {
  return parcel.status === "expected"
    ? "bg-green-100 border-green-200"
    : "bg-yellow-100 border-yellow-400";
};
</script>
<template>
  <div class="mx-auto max-w-4xl">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Parcel Receiving</h1>
      <p class="mt-1 text-gray-500">
        Scan or enter the Tracking number to mark it as received.
      </p>
    </div>

    <div class="mb-6 rounded-xl border border-gray-200 bg-white p-6">
      <div class="relative">
        <QrCodeIcon
          class="absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400"
        />
        <FormKit
          id="parcelInput"
          v-model="scanInput"
          type="text"
          placeholder="Scan Tracking Number "
          :disabled="isScanning"
          @keydown.enter.prevent="handleScan"
          @blur="handleScan"
          outer-class="$reset"
          wrapper-class="$reset"
          input-class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ram-red focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>
    </div>

    <div class="rounded-xl border border-gray-200 bg-white">
      <div class="border-b border-gray-200 p-4">
        <h2 class="font-semibold text-gray-900">Recently Scanned</h2>
      </div>

      <div
        v-if="recentlyScanned.length === 0"
        class="p-8 text-center text-gray-500"
      >
        No parcels scanned yet.
      </div>

      <div v-else class="divide-y divide-gray-200">
        <div
          v-for="parcel in recentlyScanned"
          :key="parcel.id"
          :class="[
            'flex items-center justify-between p-4',
            getStatusClass(parcel),
          ]"
        >
          <div>
            <p class="font-mono font-medium text-gray-900">
              {{ parcel.consignmentNo }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <CheckCircleIcon
              v-if="parcel.status === 'expected'"
              class="h-5 w-5 text-green-500"
            />
            <ExclamationTriangleIcon v-else class="h-5 w-5 text-yellow-600" />

            <span
              :class="
                parcel.status === 'expected'
                  ? 'text-sm text-green-500'
                  : 'text-sm text-yellow-600'
              "
            >
              Received
            </span>
          </div>
        </div>
      </div>
    </div>
    <Modal
      :is-open="showUnexpectedModal"
      title="Unexpected Parcel"
      @close="closeUnexpectedModal"
    >
      <div class="space-y-4">
        <div class="flex items-center gap-3 rounded-lg bg-yellow-50 p-3">
          <ExclamationTriangleIcon class="h-5 w-5 text-yellow-600" />
          <p class="text-sm text-yellow-800">
            Enter details for consignment:
            <strong>{{ unexpectedConsignmentNo }}</strong>
          </p>
        </div>

        <FormKit
          v-model="recipientName"
          type="text"
          label="Recipient Name & Surname*"
          placeholder="Enter recipient name"
          validation="required"
          maxlength="60"
          :validation-messages="{
            required: 'Recipient name is required',
            length: 'Name cannot exceed 60 characters',
          }"
          label-class="block text-sm font-medium text-gray-700 mb-1"
          input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
          messages-class="text-sm text-red-500 mt-1"
        />

        <FormKit
          v-model="contactNumber"
          type="text"
          v-limit="10"
          label="Contact Number*"
          placeholder="Enter contact number"
          validation="required|matches:/^[0-9]{10}$/"
          :validation-messages="{
            matches: 'Contact number must be exactly 10 digits',
            required: 'Contact number is required',
          }"
          label-class="block text-sm font-medium text-gray-700 mb-1"
          input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
          messages-class="text-sm text-red-500 mt-1"
        />
      </div>

      <template #footer>
        <Button variant="secondary" @click="closeUnexpectedModal">
          Cancel
        </Button>
        <Button
          @click="handleUnexpectedSubmit"
          :disabled="!recipientName || !/^[0-9]{10}$/.test(contactNumber)"
          variant="ram"
          :loading="isScanning"
        >
          Save Parcel
        </Button>
      </template>
    </Modal>
  </div>
</template>
